// src/pages/HelpCenterPage.jsx
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Rating
} from '@mui/material';
import {
  ExpandMore,
  Search,
  ContactSupport,
  Email,
  Phone,
  Chat,
  Article,
  VideoLibrary,
  TrendingUp,
  Task,
  Payment,
  AccountCircle,
  Security,
  HelpOutline,
  Send,
  Error,
  Warning,
  Info,
  LowPriority,
  PriorityHigh
} from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';

export default function HelpCenterPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedAccordion, setExpandedAccordion] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [contactMessage, setContactMessage] = useState('');
  const [faqCategories, setFaqCategories] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [popularArticles, setPopularArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // New state for urgency/priority system
  const [urgencyLevel, setUrgencyLevel] = useState('normal');
  const [category, setCategory] = useState('general');
  const [contactReason, setContactReason] = useState('');

  // Fetch data from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        // Fetch FAQ categories
        const categoriesResponse = await fetch('https://propertyearn.net/api/api/help/faq-categories', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!categoriesResponse.ok) throw new Error('Failed to fetch categories');
        const categoriesData = await categoriesResponse.json();
        
        // Fetch all FAQs
        const faqsResponse = await fetch('https://propertyearn.net/api/api/help/faqs', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!faqsResponse.ok) throw new Error('Failed to fetch FAQs');
        const faqsData = await faqsResponse.json();
        
        // Fetch popular articles
        const articlesResponse = await fetch('https://propertyearn.net/api/api/help/popular-articles', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!articlesResponse.ok) throw new Error('Failed to fetch popular articles');
        const articlesData = await articlesResponse.json();
        
        setFaqCategories(categoriesData.categories);
        setFaqs(faqsData.faqs);
        setPopularArticles(articlesData.articles);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Handle accordion expansion
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  // Filter FAQs based on search and category
  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category.slug === activeCategory;
    const matchesSearch = searchQuery === '' || 
                         faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });


  // Helper function to get category icon
  const getCategoryIcon = (iconName) => {
    const iconMap = {
      'TrendingUp': <TrendingUp />,
      'Task': <Task />,
      'Payment': <Payment />,
      'AccountCircle': <AccountCircle />,
      'Security': <Security />
    };
    return iconMap[iconName] || <HelpOutline />;
  };

  // Group FAQs by category for display when not searching
  const faqsByCategory = {};
  faqs.forEach(faq => {
    if (!faqsByCategory[faq.category.slug]) {
      faqsByCategory[faq.category.slug] = {
        name: faq.category.name,
        icon: getCategoryIcon(faq.category.icon),
        questions: []
      };
    }
    faqsByCategory[faq.category.slug].questions.push(faq);
  });

  // Get urgency icon and color
  const getUrgencyIcon = (level) => {
    switch (level) {
      case 'low':
        return { icon: <LowPriority />, color: 'success' };
      case 'normal':
        return { icon: <Info />, color: 'info' };
      case 'high':
        return { icon: <Warning />, color: 'warning' };
      case 'urgent':
        return { icon: <PriorityHigh />, color: 'error' };
      default:
        return { icon: <Info />, color: 'info' };
    }
  };

  // Get urgency description
  const getUrgencyDescription = (level) => {
    switch (level) {
      case 'low':
        return 'General inquiry, no time sensitivity';
      case 'normal':
        return 'Standard question, response within 24 hours';
      case 'high':
        return 'Important issue, need prompt assistance';
      case 'urgent':
        return 'Critical problem requiring immediate attention';
      default:
        return 'Standard priority';
    }
  };

  // Get response time estimate
  const getResponseTime = (level) => {
    switch (level) {
      case 'low':
        return 'Within 48 hours';
      case 'normal':
        return 'Within 24 hours';
      case 'high':
        return 'Within 6 hours';
      case 'urgent':
        return 'Within 1 hour';
      default:
        return 'Within 24 hours';
    }
  };

  // Handle FAQ feedback
  const handleFeedback = async (faqId, helpful) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://propertyearn.net/api/api/help/faqs/${faqId}/feedback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ helpful })
      });
      
      if (!response.ok) throw new Error('Failed to submit feedback');
      
      alert(`Thank you for your feedback! This was marked as ${helpful ? 'helpful' : 'not helpful'}.`);
    } catch (err) {
      setError(err.message);
    }
  };

// handleContactSubmit function
// Update the handleContactSubmit function
const handleContactSubmit = async (e) => {
  e.preventDefault();
  
  if (!contactMessage.trim()) {
    setError('Please enter your message');
    return;
  }

  if (!contactReason.trim()) {
    setError('Please specify the reason for your contact');
    return;
  }

  setSending(true);
  setError('');
  setSuccessMessage('');

  try {
    const token = localStorage.getItem('token');
    
    // Use the new thread-based endpoint
    const response = await fetch('https://propertyearn.net/api/api/chat/start-conversation', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        message: `${contactReason}: ${contactMessage}`,
        category: category,
        urgency: urgencyLevel
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to start conversation');
    }
    
    const result = await response.json();
    
    setSuccessMessage('New conversation started! Our support team will respond soon. You can continue this conversation using the chat icon in the header.');
    
    // Clear form
    setContactMessage('');
    setContactReason('');
    setCategory('general');
    setUrgencyLevel('normal');
    
  } catch (err) {
    setError(err.message);
  } finally {
    setSending(false);
  }
};

  // Support contact options
  const supportOptions = [
    {
      type: 'email',
      title: 'Email Support',
      description: 'Get detailed help from our support team',
      icon: <Email color="primary" />,
      responseTime: 'Within 24 hours',
      action: 'Send us an email'
    },
    {
      type: 'chat',
      title: 'Live Chat',
      description: 'Instant help from our support agents',
      icon: <Chat color="secondary" />,
      responseTime: 'Within 5 minutes',
      action: 'Start chat',
      available: 'Mon-Fri, 9AM-6PM GMT'
    },
    {
      type: 'phone',
      title: 'Phone Support',
      description: 'Talk directly with our support team',
      icon: <Phone color="success" />,
      responseTime: 'Immediate',
      action: 'Call us',
      available: 'Mon-Fri, 9AM-6PM GMT',
      number: '+44 800 123 4567'
    }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Help Center
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Find answers to common questions or get in touch with our support team
        </Typography>
      </Box>

      {/* Search Section */}
      <Paper sx={{ p: 3, mb: 4, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
        <ContactSupport sx={{ fontSize: 48, mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          How can we help you today?
        </Typography>
        <TextField
          fullWidth
          placeholder="Search for answers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            maxWidth: 600,
            mx: 'auto',
            mt: 2,
            '& .MuiOutlinedInput-root': {
              bgcolor: 'white',
              borderRadius: 2
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Grid container spacing={4}>
        {/* Main Content - FAQs and Help Articles */}
        <Grid item xs={12} lg={8}>
          {/* Category Filter Chips */}
          <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label="All Categories"
              onClick={() => setActiveCategory('all')}
              color={activeCategory === 'all' ? 'primary' : 'default'}
              variant={activeCategory === 'all' ? 'filled' : 'outlined'}
            />
            {faqCategories.map((category) => (
              <Chip
                key={category.slug}
                icon={getCategoryIcon(category.icon)}
                label={category.name}
                onClick={() => setActiveCategory(category.slug)}
                color={activeCategory === category.slug ? 'primary' : 'default'}
                variant={activeCategory === category.slug ? 'filled' : 'outlined'}
              />
            ))}
          </Box>

          {/* Search Results */}
          {searchQuery && (
            <Typography variant="h6" gutterBottom>
              {filteredFAQs.length} results for "{searchQuery}"
            </Typography>
          )}

          {/* FAQs Accordion */}
          {filteredFAQs.length > 0 ? (
            <Box>
              {filteredFAQs.map((faq, index) => (
                <Accordion
                  key={faq.id}
                  expanded={expandedAccordion === `panel-${faq.id}`}
                  onChange={handleAccordionChange(`panel-${faq.id}`)}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box>
                      <Typography fontWeight="medium">{faq.question}</Typography>
                      <Chip
                        label={faq.category.name}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mt: 0.5, fontSize: '0.7rem' }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography color="text.secondary">
                      {faq.answer}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<HelpOutline />}
                      sx={{ mt: 2 }}
                      onClick={() => handleFeedback(faq.id, true)}
                    >
                      Was this helpful?
                    </Button>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ) : searchQuery ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No results found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try different keywords or contact our support team for help
              </Typography>
            </Paper>
          ) : (
            // Show all categories when no search
            Object.entries(faqsByCategory).map(([slug, category]) => (
              <Card key={slug} sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {category.icon}
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {category.name}
                    </Typography>
                  </Box>
                  <List>
                    {category.questions.map((question) => (
                      <ListItem
                        key={question.id}
                        button
                        onClick={() => setExpandedAccordion(`panel-${question.id}`)}
                      >
                        <ListItemIcon>
                          <HelpOutline color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={question.question} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            ))
          )}

        </Grid>

        {/* Sidebar - Support Options */}
        <Grid item xs={12} lg={4}>
          {/* Quick Contact Form with Urgency Selection */}
            <Paper sx={{ p: 3 }} id="contact-form"> {/* ADD ID FOR DIRECT LINKING */}
              <Typography variant="h6" gutterBottom>
                Send us a Message
              </Typography>
              
              {/* Add success message state */}
              {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
                  {successMessage}
                </Alert>
              )}
              
              <form onSubmit={handleContactSubmit}>
              {/* Category Selection */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={category}
                  label="Category"
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <MenuItem value="general">General Inquiry</MenuItem>
                  <MenuItem value="technical">Technical Issue</MenuItem>
                  <MenuItem value="billing">Billing & Payments</MenuItem>
                  <MenuItem value="account">Account Issues</MenuItem>
                  <MenuItem value="verification">Verification Help</MenuItem>
                  <MenuItem value="tasks">Task Related</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              {/* Urgency Level Selection */}
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                How urgent is your issue?
              </Typography>
              
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={urgencyLevel}
                  onChange={(e) => setUrgencyLevel(e.target.value)}
                >
                  {['low', 'normal', 'high', 'urgent'].map((level) => {
                    const urgencyInfo = getUrgencyIcon(level);
                    return (
                      <Paper key={level} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                        <FormControlLabel
                          value={level}
                          control={<Radio />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ color: `${urgencyInfo.color}.main`, mr: 1 }}>
                                {urgencyInfo.icon}
                              </Box>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {level.charAt(0).toUpperCase() + level.slice(1)} Priority
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {getUrgencyDescription(level)} • {getResponseTime(level)}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </Paper>
                    );
                  })}
                </RadioGroup>
              </FormControl>

              {/* Reason for Contact */}
              <TextField
                fullWidth
                label="What is this regarding?"
                value={contactReason}
                onChange={(e) => setContactReason(e.target.value)}
                placeholder="Briefly describe the reason for your contact..."
                sx={{ mb: 2, mt: 2 }}
                required
              />

              {/* Message */}
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Describe your issue or question in detail..."
                value={contactMessage}
                onChange={(e) => setContactMessage(e.target.value)}
                sx={{ mb: 2 }}
                required
              />

              {/* Urgency Summary */}
              {urgencyLevel && (
                <Alert 
                  severity={urgencyLevel === 'urgent' ? 'error' : 
                           urgencyLevel === 'high' ? 'warning' : 
                           urgencyLevel === 'normal' ? 'info' : 'success'}
                  sx={{ mb: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getUrgencyIcon(urgencyLevel).icon}
                    <Box sx={{ ml: 1 }}>
                      <Typography variant="subtitle2">
                        {urgencyLevel.charAt(0).toUpperCase() + urgencyLevel.slice(1)} Priority
                      </Typography>
                      <Typography variant="caption">
                        Expected response: {getResponseTime(urgencyLevel)}
                      </Typography>
                    </Box>
                  </Box>
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={sending || !contactMessage.trim() || !contactReason.trim()}
                startIcon={sending ? <CircularProgress size={16} /> : <Send />}
              >
                {sending ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </Paper>
            {/* Popular Articles */}
          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            Popular Help Articles
          </Typography>
          <Grid container spacing={2}>
            {popularArticles.map((article, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card variant="outlined" sx={{ height: '100%', '&:hover': { boxShadow: 2 } }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {article.title}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip label={article.category} size="small" />
                      <Typography variant="caption" color="text.secondary">
                        {article.views} views
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
        
      </Grid>
    </DashboardLayout>
  );
}